import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { ACCENTS, DEFAULT_ACCENT, SURFACES, ThemeMode } from "./palettes";

/*
 * Theme = accent colour + light/dark.
 * - Global accent: set from the marketplace (Eatzy) client login and stored
 *   in the backend (Store/AdminPanelTheme). It always colours the identifier
 *   and login screens, and is the panel colour for anyone who hasn't picked
 *   their own. Cached in localStorage so pages don't flash the default.
 * - Personal choice: per browser, in localStorage. accent null = follow global.
 * Publishes the active palette as CSS variables on <html> (var(--brand),
 * var(--app-surface), ...; --login-brand* for the auth screens) and sets
 * <html data-theme="dark|light">.
 */

const STORAGE_KEY = "admin_theme";
const GLOBAL_KEY = "admin_theme_global";
const GLOBAL_URL = `${process.env.REACT_APP_API_URL || ""}Store/AdminPanelTheme/Get/`;
const GLOBAL_EVENT = "admin-theme-global";

export interface ThemePrefs {
  accent: string | null;
  mode: ThemeMode;
}

interface ActiveTheme {
  accent: string;
  mode: ThemeMode;
}

function readStorage(key: string) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // Storage blocked - the choice just won't survive a reload.
  }
}

export function readThemePrefs(): ThemePrefs {
  try {
    const saved = JSON.parse(readStorage(STORAGE_KEY) || "null");
    return {
      accent: saved && ACCENTS[saved.accent] ? saved.accent : null,
      mode: saved && saved.mode === "dark" ? "dark" : "light",
    };
  } catch {
    return { accent: null, mode: "light" };
  }
}

export function readGlobalAccent(): string {
  const cached = readStorage(GLOBAL_KEY);
  return cached && ACCENTS[cached] ? cached : DEFAULT_ACCENT;
}

function accentVars(prefix: string, key: string, mode: ThemeMode) {
  const a = ACCENTS[key] || ACCENTS[DEFAULT_ACCENT];
  return {
    [`--${prefix}`]: a.main,
    [`--${prefix}-dark`]: mode === "dark" ? a.light : a.dark,
    [`--${prefix}-light`]: a.light,
    [`--${prefix}-rgb`]: a.rgb,
  };
}

export function applyLoginVars(globalAccent: string) {
  const root = document.documentElement;
  Object.entries(accentVars("login-brand", globalAccent, "light")).forEach(([k, v]) => root.style.setProperty(k, v));
}

export function applyThemeVars({ accent, mode }: ActiveTheme) {
  const s = SURFACES[mode];
  const root = document.documentElement;
  const vars: Record<string, string> = {
    ...accentVars("brand", accent, mode),
    "--primary-color": (ACCENTS[accent] || ACCENTS[DEFAULT_ACCENT]).main,
    "--app-bg": s.pageBg,
    "--app-surface": s.surface,
    "--app-surface-2": s.surface2,
    "--app-border": s.border,
    "--app-text": s.text,
    "--app-muted": s.muted,
    "--app-heading": s.heading,
    "--app-shadow": s.shadow,
  };
  Object.entries(vars).forEach(([k, v]) => root.style.setProperty(k, v));
  // Dark-only overrides for the older pages' hard-coded colours, written as
  // var(--dk-surface, #fff): unset in light mode, so the fallback (the
  // original colour) applies and light mode is unchanged.
  const dark: Record<string, string> = {
    "--dk-surface": s.surface,
    "--dk-surface-2": s.surface2,
    "--dk-text": s.text,
    "--dk-muted": s.muted,
    "--dk-border": s.border,
  };
  Object.entries(dark).forEach(([k, v]) =>
    mode === "dark" ? root.style.setProperty(k, v) : root.style.removeProperty(k));
  root.dataset.theme = mode;
  root.style.colorScheme = mode;
}

function resolve(prefs: ThemePrefs, globalAccent: string): ActiveTheme {
  return { accent: prefs.accent || globalAccent, mode: prefs.mode };
}

// Store a freshly fetched/saved global accent and tell any mounted provider.
export function setGlobalAccentLocally(accent: string) {
  if (!ACCENTS[accent]) return;
  writeStorage(GLOBAL_KEY, accent);
  applyLoginVars(accent);
  window.dispatchEvent(new CustomEvent(GLOBAL_EVENT, { detail: accent }));
}

// Apply before the first render so the page never flashes the default theme,
// then refresh the global accent from the backend (public endpoint).
applyLoginVars(readGlobalAccent());
applyThemeVars(resolve(readThemePrefs(), readGlobalAccent()));
fetch(GLOBAL_URL)
  .then((r) => (r.ok ? r.json() : null))
  .then((body) => {
    const accent = body?.data?.accent;
    if (accent) setGlobalAccentLocally(accent);
  })
  .catch(() => undefined);

interface ThemePrefsContextValue extends ActiveTheme {
  personalAccent: string | null; // null = following the global colour
  globalAccent: string;
  setAccent: (accent: string | null) => void;
  setMode: (mode: ThemeMode) => void;
}

const ThemePrefsContext = createContext<ThemePrefsContextValue>({
  accent: DEFAULT_ACCENT,
  mode: "light",
  personalAccent: null,
  globalAccent: DEFAULT_ACCENT,
  setAccent: () => undefined,
  setMode: () => undefined,
});

export function ThemePrefsProvider({ children }: { children: React.ReactNode }) {
  const [prefs, setPrefs] = useState<ThemePrefs>(readThemePrefs);
  const [globalAccent, setGlobalAccent] = useState<string>(readGlobalAccent);
  // Printouts always use the light theme; the saved choice is untouched.
  const [printing, setPrinting] = useState(false);

  useEffect(() => {
    writeStorage(STORAGE_KEY, JSON.stringify(prefs));
  }, [prefs]);

  useEffect(() => {
    const onGlobal = (e: Event) => setGlobalAccent((e as CustomEvent).detail);
    window.addEventListener(GLOBAL_EVENT, onGlobal);
    // The fetch above may have finished before this provider mounted.
    setGlobalAccent(readGlobalAccent());
    return () => window.removeEventListener(GLOBAL_EVENT, onGlobal);
  }, []);

  const active = useMemo<ActiveTheme>(() => {
    const t = resolve(prefs, globalAccent);
    return printing ? { ...t, mode: "light" } : t;
  }, [prefs, globalAccent, printing]);

  // Layout effect: runs inside the flushSync below, before the print snapshot.
  useLayoutEffect(() => {
    applyThemeVars(active);
  }, [active]);

  useEffect(() => {
    const before = () => flushSync(() => setPrinting(true));
    const after = () => setPrinting(false);
    window.addEventListener("beforeprint", before);
    window.addEventListener("afterprint", after);
    return () => {
      window.removeEventListener("beforeprint", before);
      window.removeEventListener("afterprint", after);
    };
  }, []);

  const setAccent = useCallback((accent: string | null) => {
    if (accent === null || ACCENTS[accent]) setPrefs((p) => ({ ...p, accent }));
  }, []);
  const setMode = useCallback((mode: ThemeMode) => setPrefs((p) => ({ ...p, mode })), []);

  const value = useMemo(
    () => ({ ...active, personalAccent: prefs.accent, globalAccent, setAccent, setMode }),
    [active, prefs.accent, globalAccent, setAccent, setMode],
  );
  return <ThemePrefsContext.Provider value={value}>{children}</ThemePrefsContext.Provider>;
}

export const useThemePrefs = () => useContext(ThemePrefsContext);

// Real hex values for places CSS variables can't reach (SVG attributes,
// colour maths like `${hex}14`).
export function useBrandHex() {
  const { accent, mode } = useThemePrefs();
  const a = ACCENTS[accent] || ACCENTS[DEFAULT_ACCENT];
  return { ...a, surfaces: SURFACES[mode] };
}
